'use no memo';

// Memoization prevents to load svg icons after component's first render
import {skipToken} from '@reduxjs/toolkit/query';
import {Alert, Button, Card, DataStatus, Rank} from 'platform/components';
import {Box, Heading, HStack, Icon, Link, Show, Space, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {PhotoCheckerMode} from '@dms/api/core';
import {getApiErrorMessages} from '@dms/api/utils';
import {
  saleVehiclePhotoApi,
  useCreateSaleVehicleAlbumPhotoCheckerReportMutation,
  useGetSaleVehicleAlbumPhotoCheckerReportQuery,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError, is404ApiError, usePermissions} from '@dms/shared';

import {ApiError, TestIdProps} from 'shared/web';

import {PhotoCheckerSilhouette} from './PhotoCheckerSilhouette';
import {getPhotoCheckerPendingAlertContent} from './utils/getPhotoCheckerPendingAlertContent';
import {getPhotoCheckerRankSurfaceColors} from './utils/getPhotoCheckerRankSurfaceColors';
import {isPhotoCheckerAutoModeMissed} from './utils/isPhotoCheckerAutoModeMissed';
import {
  arePhotosNewerThanReportSnapshot,
  getAlbumPhotosReferenceAt,
  getPhotoCheckerReportCreatedAt,
  isPhotoCheckerResultStale,
} from './utils/isPhotoCheckerResultStale';
import {parseInlineMarkdown} from './utils/parseInlineMarkdown';

const PHOTO_CHECKER_POLLING_INTERVAL = 5_000;

const PHOTO_CHECKER_BASE_URL =
  process.env.ENV_TYPE === 'prod'
    ? 'https://photochecker.carsdata.com'
    : 'https://photocheckerv3.carsdata.dev';

interface PhotoCheckerWidgetProps extends TestIdProps {
  saleVehicleAlbumId: string;
  photosUpdatedAt?: string | null;
  albumCreatedAt?: string | null;
  automaticModeEnabledAt?: string | null;
  mode: PhotoCheckerMode;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function PhotoCheckerWidget(props: PhotoCheckerWidgetProps) {
  const [canCreatePhotoCheckerReport] = usePermissions({
    permissionKeys: ['createPhotoCheckerReport'],
  });

  const cachedResult =
    saleVehiclePhotoApi.endpoints.getSaleVehicleAlbumPhotoCheckerReport.useQueryState({
      saleVehicleAlbumId: props.saleVehicleAlbumId,
    });
  const pollingInterval =
    cachedResult?.data?.status === 'processing' ? PHOTO_CHECKER_POLLING_INTERVAL : 0;

  const {data, isLoading, isError, error, refetch} = useGetSaleVehicleAlbumPhotoCheckerReportQuery(
    isNotNil(props.saleVehicleAlbumId) ? {saleVehicleAlbumId: props.saleVehicleAlbumId} : skipToken,
    {pollingInterval}
  );

  const [createReport, {isLoading: isCreating}] =
    useCreateSaleVehicleAlbumPhotoCheckerReportMutation();

  const handleRecheck = () => {
    createReport({saleVehicleAlbumId: props.saleVehicleAlbumId}).unwrap().catch(handleApiError);
  };

  const isNotFound = isError && is404ApiError(error);

  const reportUrl = data?.recordId ? `${PHOTO_CHECKER_BASE_URL}/${data.recordId}` : null;

  const overallScore = data?.result?.overall_score;
  const {backgroundColor: scoreBgColor, borderColor: scoreBorderColor} =
    getPhotoCheckerRankSurfaceColors(overallScore);

  const issues = data?.result?.gallery_evaluation?.gallery_summary ?? [];
  const evals = data?.result?.evals ?? [];

  const isPhotoCheckerError = data?.status === 'failed' || (isError && !isNotFound);
  const apiErrorMessages = getApiErrorMessages(error as ApiError);
  const apiErrorText: string = Array.isArray(apiErrorMessages)
    ? apiErrorMessages.join('\n')
    : apiErrorMessages;
  const failedReportMessage =
    data?.status === 'failed' && isNotNil(data?.message) ? data.message : undefined;
  const photoCheckerErrorMessage =
    failedReportMessage ?? apiErrorText ?? i18n.t('entity.photoChecker.labels.failed');

  const photosReferenceAt = getAlbumPhotosReferenceAt(props.photosUpdatedAt, props.albumCreatedAt);

  const reportCreatedAt = getPhotoCheckerReportCreatedAt(data?.createdAt, data?.status);

  const isResultStale = isPhotoCheckerResultStale(
    props.photosUpdatedAt,
    props.albumCreatedAt,
    reportCreatedAt,
    data?.status
  );

  const isAutoModeMissedBase = isPhotoCheckerAutoModeMissed(
    props.photosUpdatedAt,
    props.albumCreatedAt,
    props.automaticModeEnabledAt,
    reportCreatedAt,
    props.mode
  );

  // report.createdAt is the photos snapshot time — only missed when no report exists or the
  // snapshot predates current photos (same comparison as isPhotoCheckerResultStale).
  const isAutoModeMissedCheck =
    isAutoModeMissedBase &&
    (isNotFound ||
      arePhotosNewerThanReportSnapshot(
        props.photosUpdatedAt,
        props.albumCreatedAt,
        reportCreatedAt
      ));

  const isPendingFirstCheck = isNotFound && isNotNil(photosReferenceAt);
  const showCheckPendingAlert =
    data?.status !== 'processing' &&
    (isResultStale || isPendingFirstCheck || isAutoModeMissedCheck);

  const showManualActionButton = props.mode === 'manual';
  const showAutomaticHelperText =
    !showManualActionButton && data?.status !== 'processing' && !isAutoModeMissedCheck;

  const pendingAlertContent = getPhotoCheckerPendingAlertContent({
    isResultStale,
    isAutoModeMissedCheck,
    showManualActionButton,
    isNotFound,
  });

  return (
    <Card
      title={i18n.t('entity.photoChecker.labels.photoChecker')}
      actions={
        reportUrl
          ? [
              {
                type: 'custom',
                component: (
                  <Link
                    href={reportUrl}
                    target="_blank"
                    size="small"
                    leftIcon="action/open_in_new"
                    title={i18n.t('general.actions.showDetail')}
                  />
                ),
              },
            ]
          : undefined
      }
    >
      <DataStatus
        isLoading={isLoading}
        isError={isPhotoCheckerError}
        errorMessage={photoCheckerErrorMessage}
        errorAction={{
          title: i18n.t('general.actions.retry'),
          variant: 'ghostLink',
          leftIcon: 'navigation/refresh',
          onClick: data?.status === 'failed' ? handleRecheck : refetch,
        }}
      >
        <VStack spacing={4} align="center">
          <Text size="xSmall" color="tertiary">
            {i18n.t('entity.photoChecker.labels.description')}
          </Text>

          <Show when={data?.status === 'processing'}>
            <HStack spacing={2} align="center">
              <Icon value="action/autorenew" color="severity.informational" />
              <Text size="xSmall" color="tertiary">
                {i18n.t('entity.photoChecker.labels.processing')}
              </Text>
            </HStack>
          </Show>

          <Show when={showCheckPendingAlert}>
            <Alert
              variant="warning"
              layout="detailed"
              message={i18n.t(pendingAlertContent.messageKey)}
              secondaryMessage={
                pendingAlertContent.secondaryMessage
                  ? i18n.t(pendingAlertContent.secondaryMessage)
                  : undefined
              }
              actions={[
                {
                  title: i18n.t(pendingAlertContent.actionKey),
                  variant: 'secondary',
                  leftIcon: 'action/ai',
                  isLoading: isCreating || data?.status === 'processing',
                  isDisabled: !canCreatePhotoCheckerReport,
                  onClick: handleRecheck,
                },
              ]}
            />
          </Show>

          {isNotNil(overallScore) && data?.status === 'completed' ? (
            <>
              <Box
                width="100%"
                borderRadius="medium"
                padding={4}
                backgroundColor={scoreBgColor}
                border="1px solid"
                borderColor={scoreBorderColor}
              >
                <VStack spacing={3} width="100%">
                  <HStack spacing={2} align="center" wrap>
                    <Heading size={3}>{i18n.t('entity.photoChecker.labels.overallScore')}</Heading>
                    <HStack spacing={2} align="center">
                      <Heading size={3}>{`${overallScore}%`}</Heading>
                      <Rank percentage={overallScore ?? null} />
                    </HStack>
                  </HStack>
                  <Show when={issues.length > 0}>
                    <VStack spacing={1} width="100%">
                      {issues.map((issue: string) => (
                        <HStack key={issue} spacing={2} align="flex-start" width="100%">
                          <Text size="xSmall">•</Text>
                          <Box flex={1} minWidth={0}>
                            <Text size="xSmall">{parseInlineMarkdown(issue)}</Text>
                          </Box>
                        </HStack>
                      ))}
                    </VStack>
                  </Show>
                </VStack>
              </Box>
              <PhotoCheckerSilhouette evals={evals} />
            </>
          ) : (
            <PhotoCheckerSilhouette />
          )}

          <Space />

          <Show when={showManualActionButton}>
            <Button
              title={match<boolean, string>(isNotFound)
                .with(true, () => i18n.t('entity.photoChecker.actions.checkPhotoQuality'))
                .with(false, () => i18n.t('entity.photoChecker.actions.recheckPhotoQuality'))
                .exhaustive()}
              variant="secondary"
              leftIcon="action/ai"
              iconColor="text.primary"
              isLoading={isCreating || data?.status === 'processing'}
              isDisabled={!canCreatePhotoCheckerReport}
              onClick={handleRecheck}
            />
          </Show>
          <Show when={showAutomaticHelperText}>
            <Text size="xSmall" color="tertiary" align="center">
              {i18n.t('entity.photoChecker.labels.checkRunsAutomatically')}
            </Text>
          </Show>
        </VStack>
      </DataStatus>
    </Card>
  );
}
